import apiClient from './client';

const programParams = (options = {}) => (
  options.programId ? { params: { program_id: options.programId } } : {}
);

const withProgramBody = (data = {}, options = {}) => ({
  ...data,
  ...(options.programId ? { program_id: options.programId } : {})
});

export const scenariosApi = {
  // List all scenarios for a part
  getScenarios: async (partNumber, options = {}) => {
    try {
      const response = await apiClient.get(`/api/v1/parts/${partNumber}/scenarios`, programParams(options));
      return response.data;
    } catch (error) {
      console.error('Failed to get scenarios:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Create a new scenario
  createScenario: async (partNumber, data, options = {}) => {
    try {
      const response = await apiClient.post(
        `/api/v1/parts/${partNumber}/scenarios`,
        withProgramBody(data, options),
        programParams(options)
      );
      return response.data;
    } catch (error) {
      console.error('Failed to create scenario:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Update a scenario
  updateScenario: async (partNumber, scenarioId, data, options = {}) => {
    try {
      const response = await apiClient.put(
        `/api/v1/parts/${partNumber}/scenarios/${scenarioId}`,
        withProgramBody(data, options),
        programParams(options)
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update scenario:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Delete a scenario
  deleteScenario: async (partNumber, scenarioId, options = {}) => {
    try {
      const response = await apiClient.delete(
        `/api/v1/parts/${partNumber}/scenarios/${scenarioId}`,
        programParams(options)
      );
      return response.data;
    } catch (error) {
      console.error('Failed to delete scenario:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Activate a scenario
  activateScenario: async (partNumber, scenarioId, options = {}) => {
    try {
      const response = await apiClient.post(
        `/api/v1/parts/${partNumber}/scenarios/${scenarioId}/activate`,
        withProgramBody({}, options),
        programParams(options)
      );
      return response.data;
    } catch (error) {
      console.error('Failed to activate scenario:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Duplicate a scenario
  duplicateScenario: async (partNumber, scenarioId, data, options = {}) => {
    try {
      const response = await apiClient.post(
        `/api/v1/parts/${partNumber}/scenarios/${scenarioId}/duplicate`,
        withProgramBody(data, options),
        programParams(options)
      );
      return response.data;
    } catch (error) {
      console.error('Failed to duplicate scenario:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Compare scenarios
  compareScenarios: async (partNumber, scenarioIds, options = {}) => {
    try {
      const response = await apiClient.get(`/api/v1/parts/${partNumber}/scenarios/compare`, {
        params: {
          scenario_ids: scenarioIds.join(','),
          ...(options.programId ? { program_id: options.programId } : {})
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to compare scenarios:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  /**
   * Get cost impact comparison between two scenarios for the Scenario Impact Banner
   * @param {string} partNumber - Part number
   * @param {Object} options - { currentScenarioName?, currentScenarioId?, baselineScenarioName?, baselineScenarioId? }
   * @returns {Promise<Object>} Cost impact data with component breakdowns
   */
  getCostImpact: async (partNumber, options = {}) => {
    try {
      const params = {};
      if (options.currentScenarioName) params.current_scenario_name = options.currentScenarioName;
      if (options.currentScenarioId) params.current_scenario_id = options.currentScenarioId;
      if (options.baselineScenarioName) params.baseline_scenario_name = options.baselineScenarioName;
      if (options.baselineScenarioId) params.baseline_scenario_id = options.baselineScenarioId;
      if (options.programId) params.program_id = options.programId;
      
      const response = await apiClient.get(`/api/v1/parts/${partNumber}/scenarios/cost-impact`, { params });
      return response.data;
    } catch (error) {
      console.error('Failed to get cost impact:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }
};
